<script setup lang="ts">
import ProductOption from '../../formkit/select/ProductOption.slot.vue'
import ProductOptionCompact from '../../formkit/select/ProductOptionCompact.slot.vue'

import type { WSelectProps } from './Select.vue'
import type { BaseSelect } from '~/shared/model';

const model = defineModel()
const props = defineProps<WSelectProps & { view?: 'full' | 'compact', base?: typeof BaseSelect }>()

const baseInstance = props.base ? new props.base({}) : null

const selectRef = ref(null)
const view = props.view === 'full' || !props.view ? 'full' : 'compact'

defineExpose({
    clearSelectOptions() {
      selectRef.value?.clearSelectOptions()
    },
    resolveSelectOptions() {
      selectRef.value?.resolveSelectOptions()
    },
})

if (!props.options && !baseInstance) {
  console.log(`[SelectProduct] No data source provided for select`, props)
}

</script>
<template>
  <WSelect
    v-model="model"
    :where="props.where"
    :options="props.options || baseInstance?.input().options"
    :initial-options="props.initialOptions || baseInstance.input().initialOptions"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :mode="props.mode"
    :searchable="true"
    :can-clear="false"
    :object="props.object || false"
    ref="selectRef"
    view="fulltag"
    :caret="false"
    :formkit="props.formkit"
  >
    <template #option="{ option }">
      <ProductOption v-if="view === 'full'" :key="option.product?.value" :product="option" />
      <ProductOptionCompact v-if="view === 'compact'" :key="option.product?.value" :product="option" />
    </template>

    <template #tag="{ disabled, handleTagRemove, option }">
      <ProductOption v-if="view === 'full'" :key="option.product?.value" :product="option" />
      <ProductOptionCompact v-if="view === 'compact'" :key="option.product?.value" :product="option" />

      <span
        v-if="!disabled"
        class="multiselect-tag-remove group"
        @click.prevent="handleTagRemove(option, $event)"
      >
        <div class="i-heroicons-solid-x multiselect-tag-remove-icon" />
      </span>
    </template>

    <template #singlelabel="{ value, select$ }">
      <div class="multiselect-single-label !relative">
        <ProductOption v-if="view === 'full'" class="text-black" :key="value" :product="value" />
        <ProductOptionCompact v-if="view === 'compact'" class="text-black" :key="value" :product="value" />
      </div>
      <span
          v-if="!disabled && !select$.selectRef?.isActive"
          class="multiselect-tag-remove group cursor-pointer absolute z-50"
          :class="{
            'right-1 top-1': view === 'full',
            'right-8': view === 'compact'
          }"
          @click.prevent="model = null"
        >
          <div class="i-heroicons-solid-x multiselect-tag-remove-icon !h-4 !w-4" />
      </span>
    </template>
  </WSelect>
</template>
<style lang="postcss">
.multiselect-tag-remove {
  @apply flex items-center justify-center p-1 mx-0.5 rounded-sm hover:bg-black hover:bg-opacity-5;
}
.multiselect-tag-remove-icon {
  @apply opacity-30 inline-block w-3 h-3 group-hover:opacity-70;
}
.multiselect-multiple-label,
.multiselect-single-label {
  @apply flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug px-3.5 text-sm;
}
.multiselect-single-label {
  @apply w-full;
  .multiselect-single-label-text {
    @apply overflow-hidden text-ellipsis whitespace-nowrap pr-9;
  }
}
</style>
